import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, PERIODS, filterByPeriod, formatBRL } from "../lib/constants";
import TransactionRow from "../components/TransactionRow";
import TransactionSheet from "../components/TransactionSheet";
import ConfirmVoiceSheet from "../components/ConfirmVoiceSheet";
import VoiceCapture from "../components/VoiceCapture";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";

export default function TransactionsPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("month");
  const [cat, setCat] = useState("Todas");
  const [type, setType] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetInitial, setSheetInitial] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewPayload, setReviewPayload] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/transactions"); setItems(r.data); }
    catch { toast.error("Erro ao carregar"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let arr = filterByPeriod(items, period);
    if (cat !== "Todas") arr = arr.filter((t) => t.category === cat);
    if (type !== "all") arr = arr.filter((t) => t.type === type);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter((t) => t.description.toLowerCase().includes(s) || t.category.toLowerCase().includes(s));
    }
    return arr;
  }, [items, q, period, cat, type]);

  const totals = useMemo(() => {
    const i = filtered.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const e = filtered.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    return { income: i, expense: e, balance: i - e };
  }, [filtered]);

  const onSaved = (newItem, removedId) => {
    if (removedId) setItems((p) => p.filter((t) => t.id !== removedId));
    else if (Array.isArray(newItem)) setItems((p) => [...newItem, ...p]);
    else if (newItem) setItems((p) => {
      const idx = p.findIndex((t) => t.id === newItem.id);
      if (idx >= 0) { const cp = [...p]; cp[idx] = newItem; return cp; }
      return [newItem, ...p];
    });
  };

  return (
    <div className="max-w-md mx-auto px-5 pt-8 pb-6 space-y-5">
      <header>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Extrato</p>
        <h1 className="font-display text-2xl font-bold text-white">Movimentações</h1>
      </header>

      <div className="pnc-card p-4 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Entradas</p>
          <p className="text-sm font-mono-nums font-bold text-emerald-300 mt-1">{formatBRL(totals.income)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold">Saídas</p>
          <p className="text-sm font-mono-nums font-bold text-rose-300 mt-1">{formatBRL(totals.expense)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Saldo</p>
          <p className="text-sm font-mono-nums font-bold text-white mt-1">{formatBRL(totals.balance)}</p>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="tx-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por descrição ou categoria"
          className="w-full pl-9 pr-4 py-2.5 rounded-2xl bg-slate-900/60 border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400/60"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pnc-scroll-hide">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border ${period === p.key ? "bg-gradient-to-r from-emerald-500 to-emerald-600 border-emerald-500 text-slate-900 shadow-lg shadow-emerald-500/25" : "bg-slate-900/50 border-white/10 text-slate-300 hover:text-white"}`}
          >{p.label}</button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pnc-scroll-hide">
        {["all", "income", "expense"].map((t) => (
          <button
            key={t}
            data-testid={`type-${t}`}
            onClick={() => setType(t)}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border ${type === t ? "bg-white/95 border-white text-slate-900" : "bg-slate-900/50 border-white/10 text-slate-300 hover:text-white"}`}
          >{t === "all" ? "Todas" : t === "income" ? "Entradas" : "Saídas"}</button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pnc-scroll-hide">
        {["Todas", ...CATEGORIES].map((c) => (
          <button
            key={c}
            data-testid={`cat-${c}`}
            onClick={() => setCat(c)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${cat === c ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "bg-slate-900/50 border-white/10 text-slate-400 hover:text-white"}`}
          >{c}</button>
        ))}
      </div>

      <VoiceCapture onReview={(p) => { setReviewPayload(p); setReviewOpen(true); }} />

      <button
        data-testid="add-transaction-btn"
        onClick={() => { setSheetInitial(null); setSheetOpen(true); }}
        className="btn-emerald w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-sm"
      ><Plus className="w-4 h-4" /> Nova movimentação</button>

      <div className="pnc-card p-2 stagger">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">Nenhuma movimentação encontrada.</div>
        )}
        {filtered.map((tx) => <TransactionRow key={tx.id} tx={tx} onClick={(t) => { setSheetInitial(t); setSheetOpen(true); }} />)}
      </div>

      <TransactionSheet open={sheetOpen} onClose={() => setSheetOpen(false)} initial={sheetInitial} onSaved={onSaved} />
      <ConfirmVoiceSheet open={reviewOpen} onClose={() => setReviewOpen(false)} payload={reviewPayload} onSaved={onSaved} />
    </div>
  );
}
